import { StepLayout } from 'components/StepLayout/StepLayout';
import { Text, TextButton } from '@wix/design-system';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { BI_ACTION_NAME, BI_STEP_NAME } from 'config/bi';
import { biEvents } from 'api/bi-events';
import { isExistingSiteEntryMode } from 'config/entry-mode';
import { StepId } from 'config/steps';
import { useSiteImages } from 'hooks/useSiteImages';
import { useAppStore } from 'stores/appStore';
import { useMockupStore } from 'stores/mockupStore';
import { SelectImageStepOptions } from './components/SelectImageStepOptions';
import { SelectImageStepUpload } from './components/SelectImageStepUpload';
import { TextDivider } from 'components/TextDivider/TextDivider';
import { useMockupGenerate } from 'hooks/useMockupGenerate';
import { getSelectedProductIds } from 'utils/product-selection';
import styles from './SelectImageStep.scss';

function formatContinueBiValue(mediaUrl: string | undefined, imagesSource?: string): string | undefined {
  if (!mediaUrl) {
    return undefined;
  }

  if (!imagesSource) {
    return mediaUrl;
  }

  return `${mediaUrl}|source:${imagesSource}`;
}

export const SelectImageStep: FC = () => {
  const { t } = useTranslation();

  const localFile = useAppStore((state) => state.localFile);
  const mediaUrl = useAppStore((state) => state.mediaUrl);
  const selectedProducts = useAppStore((state) => state.selectedProducts);
  const entryMode = useAppStore((state) => state.entryMode);
  const { imagesSource } = useSiteImages();

  const mockups = useMockupStore((state) => state.mockups);
  const isMockupLoading = useMockupStore((state) => state.isLoading);
  const generationFailed = useMockupStore((state) => state.generationFailed);
  const setGenerationFailed = useMockupStore((state) => state.setGenerationFailed);

  const [isLocalFileSelected, setIsLocalFileSelected] = useState<boolean>(!!localFile);

  const { generateMockups } = useMockupGenerate();

  const selectedProductIds = useMemo(() => getSelectedProductIds(selectedProducts), [selectedProducts]);

  const isContinueDisabled = useMemo(() => {
    const hasImage = Boolean(localFile || mediaUrl);
    if (!hasImage || isMockupLoading) {
      return true;
    }

    return !selectedProductIds.every((productId) => productId in mockups);
  }, [localFile, mediaUrl, isMockupLoading, mockups, selectedProductIds]);

  return (
    <StepLayout
      disclaimer={t('select-image-step.disclaimer')}
      description={t('select-image-step.description')}
      title={t('select-image-step.title')}
      nextButtonProps={{
        label: t('select-image-step.actions.primary-button'),
        disabled: isContinueDisabled,
      }}
      onNext={() => {
        biEvents.actionClicked({
          screenName: BI_STEP_NAME[StepId.SelectImage],
          action: BI_ACTION_NAME.CONTINUE,
          value: formatContinueBiValue(mediaUrl, isExistingSiteEntryMode(entryMode) ? imagesSource : undefined),
        });
      }}
      onBack={() => {
        biEvents.actionClicked({
          screenName: BI_STEP_NAME[StepId.SelectImage],
          action: BI_ACTION_NAME.BACK,
        });
      }}
    >
      {generationFailed && (
        <div className={styles.selectImageStepMockupError} role="alert">
          <Text size="small" weight="bold">
            {t('select-image-step.mockup-generation.error-message')}
          </Text>
          <TextButton size="small" onClick={() => setGenerationFailed(false)}>
            {t('select-image-step.mockup-generation.dismiss')}
          </TextButton>
        </div>
      )}
      <SelectImageStepUpload
        isSelected={isLocalFileSelected}
        onSelect={(file) => {
          setIsLocalFileSelected(true);
          generateMockups({ type: 'localFile', file });
        }}
      />
      <TextDivider text={t('select-image-step.divider')} />
      <SelectImageStepOptions
        onItemClick={(imageUrl) => {
          setIsLocalFileSelected(false);
          generateMockups({ type: 'siteImage', mediaUrl: imageUrl });
        }}
      />
    </StepLayout>
  );
};
